import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { DEFAULT_BROWSE_VIEW, readBrowseState, writeBrowseState } from '../utils/browseState';

export default function useBrowseState() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const state = useMemo(() => readBrowseState(location.search), [location.search]);
  const view = state.view || DEFAULT_BROWSE_VIEW;
  const [viewport, setViewport] = useState(null);
  const [locationValue, setLocationValue] = useState(state.where);
  const current = useRef();
  current.current = { location, state, view };
  const lastView = useRef(null);
  const urlTimer = useRef();
  const geolocationBlocked = useRef(Boolean(state.view));
  const initialHasView = useRef(Boolean(state.view));
  const previous = useRef({ key: location.key, where: state.where });
  const markInteraction = useCallback(() => { geolocationBlocked.current = true; }, []);
  const onMapInteraction = useCallback(() => {
    markInteraction();
    clearTimeout(urlTimer.current);
  }, [markInteraction]);

  useLayoutEffect(() => {
    clearTimeout(urlTimer.current);
    const isHistoryNavigation = navigationType === 'POP' && previous.current.key !== location.key;
    if (isHistoryNavigation) geolocationBlocked.current = true;
    if (isHistoryNavigation || previous.current.where !== state.where) setLocationValue(state.where);
    previous.current = { key: location.key, where: state.where };
  }, [location.key, navigationType, state.where]);

  const save = useCallback((next, replace) => {
    const { location: latestLocation } = current.current;
    const search = writeBrowseState(latestLocation.search, next);
    if (search === latestLocation.search.replace(/^\?/, '')) return;
    navigate({ pathname: latestLocation.pathname, search: search ? `?${search}` : '', hash: latestLocation.hash }, { replace });
  }, [navigate]);

  const commit = useCallback((patch) => {
    clearTimeout(urlTimer.current);
    markInteraction();
    const latest = current.current;
    const next = { ...latest.state, view: lastView.current || latest.view, ...patch };
    if (!next.date) next.includeUnavailable = false;
    if (Object.prototype.hasOwnProperty.call(patch, 'where')) setLocationValue(patch.where);
    save(next, false);
  }, [markInteraction, save]);

  const onViewChange = useCallback((nextViewport) => {
    lastView.current = nextViewport.view;
    setViewport((old) => JSON.stringify(old) === JSON.stringify(nextViewport) ? old : nextViewport);
    clearTimeout(urlTimer.current);
    const key = current.current.location.key;
    urlTimer.current = setTimeout(() => {
      if (current.current.location.key !== key) return;
      save({ ...current.current.state, view: nextViewport.view }, true);
    }, 200);
  }, [save]);

  useEffect(() => () => clearTimeout(urlTimer.current), []);

  useEffect(() => {
    if (initialHasView.current || !navigator.geolocation) return;
    let active = true;
    navigator.geolocation.getCurrentPosition((position) => {
      if (!active || geolocationBlocked.current) return;
      save({ ...current.current.state, view: { center: [position.coords.latitude, position.coords.longitude], zoom: 11 } }, true);
    }, (error) => { if (active) console.warn('Geolocation unavailable:', error.message); });
    return () => { active = false; };
  }, [save]);

  const onLocationChange = useCallback((value) => {
    markInteraction();
    setLocationValue(value);
  }, [markInteraction]);

  return { state, view, viewport, locationValue, onLocationChange, commit, onViewChange, markInteraction, onMapInteraction };
}
